// =======================
// src/paginas/adminPage/documentos/CadastroDocumentosPage.js
// =======================

import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  AddCircleRounded,
  ArrowBackRounded,
  ArrowDownward,
  ArrowUpward,
  CancelRounded,
  CloseRounded,
  DeleteRounded,
  DescriptionRounded,
  EditRounded,
  FolderCopy,
  InfoRounded,
  SaveRounded,
  Search,
  SettingsRounded,
  SubjectRounded,
  TitleRounded,
  UnfoldMore,
  VisibilityRounded,
  WarningAmberRounded,
} from "@mui/icons-material";

import ErrorState from "@componentes/ErrorWindow";
import { api } from "@api/api";
import PaginacaoLista from "../../../componentes/comuns/PaginacaoLista";

import {
  botaoAdicionar,
  botaoCancelar,
  botaoExcluir,
  botaoSalvar,
  botaoVoltar,
} from "../../../estilos/botoes";
import { ajustarTamanhoFonteSre } from "../../../estilos/global";

const OPCOES_ITENS_POR_PAGINA = [5, 10, 20];

const ESTILO_ACAO_EDITAR = {
  width: 30,
  height: 30,
  p: 0,
  color: "#087cf0",
  backgroundColor: "#eaf4ff",
  border: "1px solid #d7eaff",
  borderRadius: "50%",
  transition:
    "background-color 0.15s ease, color 0.15s ease, transform 0.15s ease",
  "&:hover": {
    color: "#0767d8",
    backgroundColor: "#dceeff",
    transform: "translateY(-1px)",
  },
};

const ESTILO_ACAO_EXCLUIR = {
  width: 30,
  height: 30,
  p: 0,
  color: "#e53935",
  backgroundColor: "#ffeded",
  border: "1px solid #ffdcdc",
  borderRadius: "50%",
  transition:
    "background-color 0.15s ease, color 0.15s ease, transform 0.15s ease",
  "&:hover": {
    color: "#c62828",
    backgroundColor: "#ffdddd",
    transform: "translateY(-1px)",
  },
};

const ESTILO_ACAO_DETALHES = {
  width: 30,
  height: 30,
  p: 0,
  color: "#087cf0",
  backgroundColor: "#eef7ff",
  border: "1px solid #d7eaff",
  borderRadius: "50%",
  transition:
    "background-color 0.15s ease, color 0.15s ease, transform 0.15s ease",
  "&:hover": {
    color: "#0767d8",
    backgroundColor: "#dceeff",
    transform: "translateY(-1px)",
  },
};

const ESTILO_CAMPO_DOCUMENTO = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.2,
    backgroundColor: "#ffffff",
    color: "#40546d",
    fontSize: ajustarTamanhoFonteSre("0.84rem"),
    "& fieldset": {
      borderColor: "#ccdbea",
    },
    "&:hover fieldset": {
      borderColor: "#9fc3e5",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#087cf0",
    },
  },
  "& .MuiInputAdornment-root": {
    color: "#526b8d",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#8a9aae",
    opacity: 1,
  },
};

const CadastroDocumentosPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // CADASTRO / EDIÇÃO
  const [mostrarCadastro, setMostrarCadastro] = useState(false);
  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [documentoSelecionado, setDocumentoSelecionado] = useState(null);

  // DADOS
  const [documentos, setDocumentos] = useState([]);

  // LISTAGEM
  const [busca, setBusca] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(5);
  const [ordem, setOrdem] = useState({
    coluna: "nome",
    direcao: "asc",
  });

  // DETALHES
  const [openDetails, setOpenDetails] = useState(false);
  const [detalhesDocumento, setDetalhesDocumento] = useState(null);
  const [abaDetalhes, setAbaDetalhes] = useState(0);
  const [processosDetalhes, setProcessosDetalhes] = useState([]);
  const [carregandoProcessosDetalhes, setCarregandoProcessosDetalhes] =
    useState(false);
  const [erroProcessosDetalhes, setErroProcessosDetalhes] = useState(false);
  const [processoDestacadoId, setProcessoDestacadoId] = useState(null);
  const processoDestacadoRef = useRef(null);

  // EXCLUSÃO
  const [documentoParaExcluir, setDocumentoParaExcluir] = useState(null);
  const [confirmacaoExclusaoAberta, setConfirmacaoExclusaoAberta] =
    useState(false);
  const [tiposProcessosVinculados, setTiposProcessosVinculados] = useState([]);
  const [isLoadingProcessosVinculados, setIsLoadingProcessosVinculados] =
    useState(false);
  const [erroConsultaProcessosVinculados, setErroConsultaProcessosVinculados] =
    useState(false);

  // CONFIRMAÇÃO FINAL
  const [confirmacaoFinalAberta, setConfirmacaoFinalAberta] = useState(false);
  const [confirmacaoFinalCiente, setConfirmacaoFinalCiente] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // FEEDBACK
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // CARREGAMENTO / ERROS
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [errorType, setErrorType] = useState(null);

  // CARREGAR DOCUMENTOS
  useEffect(() => {
    const fetchDocumentos = async () => {
      try {
        setIsLoading(true);
        setErrorType(null);

        const response = await axios.get(api.adminListDocs(), {
          withCredentials: true,
        });

        setDocumentos(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Erro ao buscar documentos:", error);
        setDocumentos([]);

        if (error.message?.includes("Network Error")) {
          setErrorType("network");
        } else if (error.response?.status === 403) {
          setErrorType("permission");
        } else {
          setErrorType("module");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocumentos();
  }, [refreshKey]);

  // PROCESSOS VINCULADOS — ABA DE DETALHES
  useEffect(() => {
    if (!openDetails || abaDetalhes !== 1 || !detalhesDocumento?.id) {
      return;
    }

    let ativo = true;
    const controller = new AbortController();

    const carregarProcessosDetalhes = async () => {
      setCarregandoProcessosDetalhes(true);
      setErroProcessosDetalhes(false);
      setProcessosDetalhes([]);

      try {
        const response = await axios.get(api.adminSearchDocsProcess(), {
          withCredentials: true,
          signal: controller.signal,
        });

        if (!ativo) return;

        if (!Array.isArray(response.data)) {
          throw new Error(
            "Resposta inválida na consulta de processos vinculados.",
          );
        }

        const processosDoDocumento = response.data
          .filter(
            (processo) =>
              Array.isArray(processo.documentos) &&
              processo.documentos.some(
                (documento) =>
                  Number(documento.id) === Number(detalhesDocumento.id),
              ),
          )
          .map((processo) => ({
            processo_id: processo.id,
            processo_nome: processo.nome,
          }));

        setProcessosDetalhes(processosDoDocumento);
      } catch (error) {
        if (!ativo || error.code === "ERR_CANCELED") return;

        console.error("Erro ao consultar processos nos detalhes:", error);
        setErroProcessosDetalhes(true);
        setProcessosDetalhes([]);
      } finally {
        if (ativo) setCarregandoProcessosDetalhes(false);
      }
    };

    carregarProcessosDetalhes();

    return () => {
      ativo = false;
      controller.abort();
    };
  }, [openDetails, abaDetalhes, detalhesDocumento?.id]);

  // POSICIONAR O PROCESSO DESTACADO APÓS CARREGAR OS VÍNCULOS
  useEffect(() => {
    if (
      openDetails &&
      abaDetalhes === 1 &&
      processoDestacadoId !== null &&
      processosDetalhes.length > 0
    ) {
      processoDestacadoRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [openDetails, abaDetalhes, processoDestacadoId, processosDetalhes]);

  // AUXILIARES
  const forceRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const limparFormularioDocumento = () => {
    setDocumentoSelecionado(null);
    setNome("");
    setDescricao("");
  };

  const limparDetalhesDocumento = () => {
    setProcessosDetalhes([]);
    setProcessoDestacadoId(null);
    setDetalhesDocumento(null);
  };

  const limparFluxoExclusao = () => {
    setConfirmacaoExclusaoAberta(false);
    setConfirmacaoFinalAberta(false);
    setDocumentoParaExcluir(null);
    setTiposProcessosVinculados([]);
    setErroConsultaProcessosVinculados(false);
    setIsLoadingProcessosVinculados(false);
    setConfirmacaoFinalCiente(false);
  };

  // DETALHES
  const handleOpenDetails = (doc) => {
    setProcessoDestacadoId(null);
    setAbaDetalhes(0);
    setProcessosDetalhes([]);
    setErroProcessosDetalhes(false);
    setCarregandoProcessosDetalhes(false);
    setDetalhesDocumento(doc);
    setOpenDetails(true);
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
  };

  // ABRIR DOCUMENTO A PARTIR DE DETALHES DO PROCESSO
  useEffect(() => {
    const documentoId = location.state?.documentoIdParaVisualizar;
    const processoId = location.state?.processoIdParaDestacar ?? null;

    if (documentoId == null || isLoading || errorType) {
      return;
    }

    const documento = documentos.find(
      (item) => Number(item.id) === Number(documentoId),
    );

    if (documento) {
      setProcessoDestacadoId(processoId);
      setAbaDetalhes(processoId !== null ? 1 : 0);
      setProcessosDetalhes([]);
      setErroProcessosDetalhes(false);
      setCarregandoProcessosDetalhes(false);
      setDetalhesDocumento(documento);
      setOpenDetails(true);
    } else {
      setSnackbarMessage("Documento vinculado não encontrado.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
    }

    // Impede reabertura automática ao retornar à página.
    navigate(location.pathname, {
      replace: true,
      state: null,
    });
  }, [location, documentos, isLoading, errorType, navigate]);

  // FORMULÁRIO
  const abrirNovoDocumento = () => {
    setDocumentoSelecionado(null);
    setNome("");
    setDescricao("");
    setMostrarCadastro(true);
  };

  const handleVoltarParaDocumentos = () => {
    if (isSubmitting) return;
    setMostrarCadastro(false);
  };

  const handleFecharModalDocumento = () => {
    handleVoltarParaDocumentos();
  };

  // CADASTRAR
  const handleCadastrarDocumento = async () => {
    if (!nome.trim()) {
      showSnackbar("O nome do documento é obrigatório.", "warning");
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await axios.post(
        api.adminRegisterDoc(),
        {
          nome: nome.trim(),
          descricao: descricao.trim(),
        },
        {
          withCredentials: true,
        },
      );

      showSnackbar(
        response.data?.message ||
          response.data?.msg ||
          "Documento cadastrado com sucesso.",
        "success",
      );

      forceRefresh();
      setMostrarCadastro(false);
    } catch (error) {
      console.error("Erro ao registrar documento:", error);

      if (error.message?.includes("Network Error")) {
        setErrorType("network");
      } else if (error.response?.status === 403) {
        setErrorType("permission");
      } else {
        const mensagem = error.response?.data?.message;

        if (mensagem) {
          showSnackbar(
            Array.isArray(mensagem) ? mensagem.join(" ") : mensagem,
            "error",
          );
        } else {
          setErrorType("module");
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // EDITAR
  const handleEditarDocumento = (id) => {
    const documento = documentos.find((doc) => doc?.id === id);

    if (!documento) return;

    setNome(documento.nome || "");
    setDescricao(documento.descricao || "");
    setDocumentoSelecionado(id);
    setMostrarCadastro(true);
  };

  const handleAtualizarDocumento = async () => {
    if (!nome.trim()) {
      showSnackbar("O nome do documento é obrigatório.", "warning");
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await axios.patch(
        api.adminUpdateDoc(documentoSelecionado),
        {
          nome: nome.trim(),
          descricao: descricao.trim(),
        },
        {
          withCredentials: true,
        },
      );

      showSnackbar(
        response.data?.message ||
          response.data?.msg ||
          "Documento atualizado com sucesso.",
        "success",
      );

      forceRefresh();
      setMostrarCadastro(false);
    } catch (error) {
      console.error("Erro ao atualizar documento:", error);

      if (error.message?.includes("Network Error")) {
        setErrorType("network");
      } else if (error.response?.status === 403) {
        setErrorType("permission");
      } else {
        const mensagem = error.response?.data?.message;

        if (mensagem) {
          showSnackbar(
            Array.isArray(mensagem) ? mensagem.join(" ") : mensagem,
            "error",
          );
        } else {
          setErrorType("module");
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSalvarOuAtualizarDocumento = async () => {
    if (documentoSelecionado !== null) {
      await handleAtualizarDocumento();
    } else {
      await handleCadastrarDocumento();
    }
  };

  const handleSubmitDocumento = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    await handleSalvarOuAtualizarDocumento();
  };

  // EXCLUSÃO — CONSULTA DE IMPACTO
  const solicitarExclusao = async (documento) => {
    setDocumentoParaExcluir(documento);
    setTiposProcessosVinculados([]);
    setErroConsultaProcessosVinculados(false);
    setConfirmacaoFinalAberta(false);
    setConfirmacaoFinalCiente(false);
    setConfirmacaoExclusaoAberta(true);
    setIsLoadingProcessosVinculados(true);

    try {
      const [respostaAnexos, respostaTipos] = await Promise.all([
        axios.get(api.adminDocumentLinkedProcesses(documento.id), {
          withCredentials: true,
        }),
        axios.get(api.adminSearchDocsProcess(), {
          withCredentials: true,
        }),
      ]);

      if (
        !Array.isArray(respostaAnexos.data?.processos) ||
        !Array.isArray(respostaTipos.data)
      ) {
        throw new Error("Resposta inválida na consulta dos processos.");
      }

      const tiposDoDocumento = respostaTipos.data
        .filter(
          (processo) =>
            Array.isArray(processo.documentos) &&
            processo.documentos.some(
              (doc) => Number(doc.id) === Number(documento.id),
            ),
        )
        .map((processo) => ({
          processo_id: processo.id,
          processo_nome: processo.nome,
        }));

      setTiposProcessosVinculados(tiposDoDocumento);
    } catch (error) {
      console.error("Erro ao consultar vínculos do documento:", error);
      setErroConsultaProcessosVinculados(true);
      setTiposProcessosVinculados([]);
    } finally {
      setIsLoadingProcessosVinculados(false);
    }
  };

  const fecharConfirmacaoExclusao = () => {
    if (isDeleting) return;
    limparFluxoExclusao();
  };

  const abrirConfirmacaoFinalExclusao = () => {
    if (
      !documentoParaExcluir ||
      isDeleting ||
      isLoadingProcessosVinculados ||
      erroConsultaProcessosVinculados
    ) {
      return;
    }

    setConfirmacaoFinalCiente(false);
    setConfirmacaoExclusaoAberta(false);
    setConfirmacaoFinalAberta(true);
  };

  const voltarParaImpactoExclusao = () => {
    if (isDeleting) return;

    setConfirmacaoFinalAberta(false);
    setConfirmacaoFinalCiente(false);
    setConfirmacaoExclusaoAberta(true);
  };

  const fecharConfirmacaoFinalExclusao = () => {
    if (isDeleting) return;
    limparFluxoExclusao();
  };

  // EXCLUSÃO — DELETE REAL
  const handleExcluirDocumento = async () => {
    const id = documentoParaExcluir?.id;

    if (!id) {
      showSnackbar("ID inválido para exclusão.", "error");
      return;
    }

    if (!confirmacaoFinalCiente || isDeleting) return;

    try {
      setIsDeleting(true);

      const response = await axios.delete(api.adminDeleteDoc(id), {
        withCredentials: true,
      });

      limparFluxoExclusao();
      forceRefresh();

      showSnackbar(
        response.data?.message ||
          response.data?.msg ||
          "Documento excluído com sucesso.",
        "success",
      );
    } catch (error) {
      console.error("Erro ao excluir documento:", error);

      if (error.message?.includes("Network Error")) {
        setErrorType("network");
      } else if (error.response?.status === 403) {
        setErrorType("permission");
      } else {
        const mensagemServidor =
          error.response?.data?.message || error.response?.data?.msg || "";

        const mensagem = Array.isArray(mensagemServidor)
          ? mensagemServidor.join(" ")
          : String(mensagemServidor);

        showSnackbar(
          mensagem || "Não foi possível excluir o documento.",
          "error",
        );
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // FILTRO E ORDENAÇÃO
  const documentosFiltrados = useMemo(() => {
    const termo = busca.trim().toLocaleLowerCase("pt-BR");

    return (documentos || [])
      .filter((doc) => {
        if (!doc) return false;
        if (!termo) return true;

        return String(doc.nome || "")
          .toLocaleLowerCase("pt-BR")
          .includes(termo);
      })
      .sort((a, b) => {
        const valorA =
          ordem.coluna === "descricao"
            ? String(a.descricao || "")
            : String(a.nome || "");

        const valorB =
          ordem.coluna === "descricao"
            ? String(b.descricao || "")
            : String(b.nome || "");

        const resultado = valorA.localeCompare(valorB, "pt-BR", {
          sensitivity: "base",
          numeric: true,
        });

        return ordem.direcao === "asc" ? resultado : -resultado;
      });
  }, [documentos, busca, ordem]);

  const ordenarPor = (coluna) => {
    setOrdem((atual) => ({
      coluna,
      direcao:
        atual.coluna === coluna && atual.direcao === "asc" ? "desc" : "asc",
    }));
    setPaginaAtual(1);
  };

  const totalPaginas = Math.max(
    1,
    Math.ceil(documentosFiltrados.length / itensPorPagina),
  );

  const paginaExibida = Math.min(paginaAtual, totalPaginas);
  const indiceInicial = (paginaExibida - 1) * itensPorPagina;

  const indiceFinal = Math.min(
    indiceInicial + itensPorPagina,
    documentosFiltrados.length,
  );

  const documentosPaginados = documentosFiltrados.slice(
    indiceInicial,
    indiceFinal,
  );

  useEffect(() => {
    setPaginaAtual((atual) => Math.min(atual, totalPaginas));
  }, [totalPaginas]);

  const renderCabecalhoOrdenavel = (titulo, coluna, Icone) => {
    const ativo = ordem.coluna === coluna;

    return (
      <TableSortLabel
        active={ativo}
        direction={ativo ? ordem.direcao : "asc"}
        onClick={() => ordenarPor(coluna)}
        IconComponent={
          ativo
            ? ordem.direcao === "asc"
              ? ArrowUpward
              : ArrowDownward
            : UnfoldMore
        }
        sx={{
          color: "#27456f",
          fontSize: ajustarTamanhoFonteSre("0.75rem"),
          fontWeight: 700,
          "&:hover": { color: "#0767d8" },
          "&.Mui-active": { color: "#27456f" },
          "& .MuiTableSortLabel-icon": {
            opacity: 1,
            color: "#385b90",
            fontSize: 15,
            ml: 0.35,
            transform: "none !important",
          },
        }}
      >
        <Box
          component="span"
          sx={{ display: "flex", alignItems: "center", gap: 0.8 }}
        >
          <Icone sx={{ color: "#087cf0", fontSize: 17, flexShrink: 0 }} />
          {titulo}
        </Box>
      </TableSortLabel>
    );
  };

  if (errorType) {
    return (
      <ErrorState
        errorType={errorType}
        onRetry={() => {
          setErrorType(null);
          setRefreshKey((prev) => prev + 1);
        }}
      />
    );
  }

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <Box
      sx={{
        width: "100%",
        py: 0,
      }}
    >
      {/* =====================================================
           CABEÇALHO
       ===================================================== */}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: {
            xs: "flex-start",
            sm: "center",
          },
          flexDirection: {
            xs: "column",
            sm: "row",
          },
          gap: 1.5,
          mb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.4,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              borderRadius: 1.5,
              color: "#087cf0",
              backgroundColor: "#e8f4ff",
            }}
          >
            <DescriptionRounded sx={{ fontSize: 27 }} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{
                color: "#0767d8",
                fontSize: {
                  xs: ajustarTamanhoFonteSre("1.35rem"),
                  md: ajustarTamanhoFonteSre("1.55rem"),
                },
                fontWeight: 700,
                lineHeight: 1.15,
              }}
            >
              Documentos
            </Typography>

            <Typography
              sx={{
                mt: 0.3,
                color: "#7b8798",
                fontSize: ajustarTamanhoFonteSre("0.8rem"),
                lineHeight: 1.35,
              }}
            >
              Gerencie os documentos disponíveis para os processos da SRE.
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          size="small"
          startIcon={<AddCircleRounded sx={{ fontSize: "18px !important" }} />}
          onClick={abrirNovoDocumento}
          sx={botaoAdicionar}
        >
          Adicionar documento
        </Button>
      </Box>

      {/* =====================================================
           BUSCA + REGISTROS POR PÁGINA
       ===================================================== */}

      <Box
        sx={{
          display: "flex",
          alignItems: {
            xs: "stretch",
            md: "center",
          },
          flexDirection: {
            xs: "column",
            md: "row",
          },
          gap: 1.25,
          px: 1.25,
          py: 0.55,
          mb: 0.5,
          border: "1px solid #d5e7f7",
          borderRadius: 1.5,
          backgroundColor: "#f7fbff",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            size="small"
            placeholder="Buscar documentos..."
            value={busca}
            onChange={(event) => {
              setBusca(event.target.value);
              setPaginaAtual(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    sx={{
                      color: "#087cf0",
                      fontSize: 18,
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              width: {
                xs: "100%",
                md: 440,
              },
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                height: 34,
                backgroundColor: "#ffffff",
                borderRadius: 1,
                "& fieldset": {
                  borderColor: "#d7e0ea",
                },
                "&:hover fieldset": {
                  borderColor: "#a9c9e8",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: ajustarTamanhoFonteSre("0.78rem"),
              },
            }}
          />
        </Box>

        <Box
          sx={{
            display: {
              xs: "none",
              md: "block",
            },
            width: "1px",
            height: 30,
            flexShrink: 0,
            backgroundColor: "#d8e7f4",
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: {
              xs: "space-between",
              md: "flex-end",
            },
            gap: 1.2,
            flexShrink: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#667b98",
              fontSize: ajustarTamanhoFonteSre("0.7rem"),
              whiteSpace: "nowrap",
            }}
          >
            Registros por página:
          </Typography>

          <Select
            size="small"
            value={itensPorPagina}
            onChange={(event) => {
              setItensPorPagina(Number(event.target.value));
              setPaginaAtual(1);
            }}
            renderValue={(valor) => `${valor} por página`}
            sx={{
              width: 160,
              height: 34,
              backgroundColor: "#ffffff",
              color: "#314d76",
              borderRadius: 1,
              fontSize: ajustarTamanhoFonteSre("0.72rem"),
              fontWeight: 500,
              "& .MuiSelect-select": {
                py: 0.55,
              },
            }}
          >
            {OPCOES_ITENS_POR_PAGINA.map((quantidade) => (
              <MenuItem key={quantidade} value={quantidade}>
                {quantidade} por página
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* =====================================================
           FAIXA SUPERIOR
       ===================================================== */}

      {!isLoading && documentosFiltrados.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "flex-start",
              sm: "center",
            },
            justifyContent: "space-between",
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            gap: 0.75,
            px: 1.5,
            py: 0.35,
            mb: 0.5,
            border: "1px solid #d7e8f8",
            borderRadius: 1.5,
            backgroundColor: "#f2f8fe",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#536b8b",
              fontSize: ajustarTamanhoFonteSre("0.72rem"),
            }}
          >
            {documentosFiltrados.length}{" "}
            {documentosFiltrados.length === 1 ? "registro" : "registros"}
          </Typography>

          <Box
            sx={{
              ml: {
                sm: "auto",
              },
            }}
          >
            <PaginacaoLista
              paginaAtual={paginaExibida}
              totalPaginas={totalPaginas}
              onChange={setPaginaAtual}
              ariaLabel="Paginação superior da lista de documentos"
            />
          </Box>
        </Box>
      )}

      {/* =====================================================
           CARREGAMENTO / VAZIO / TABELA
       ===================================================== */}

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      ) : documentosFiltrados.length === 0 ? (
        <Paper
          sx={{
            minHeight: 250,
            border: "1px solid #d9e8fa",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: 2,
            py: 4,
            boxShadow: "none",
          }}
        >
          <Box
            sx={{
              width: 70,
              height: 70,
              borderRadius: "50%",
              backgroundColor: "#eaf4ff",
              display: "grid",
              placeItems: "center",
              mb: 1.5,
            }}
          >
            <DescriptionRounded
              sx={{
                color: "#087cf0",
                fontSize: 38,
              }}
            />
          </Box>

          <Typography
            sx={{
              color: "#0767d8",
              fontSize: ajustarTamanhoFonteSre("1.1rem"),
              fontWeight: 700,
            }}
          >
            {busca.trim()
              ? "Nenhum documento encontrado"
              : "Nenhum documento cadastrado"}
          </Typography>

          <Typography
            sx={{
              color: "text.secondary",
              maxWidth: 390,
              mt: 0.5,
              mb: 2,
              fontSize: ajustarTamanhoFonteSre("0.85rem"),
            }}
          >
            {busca.trim()
              ? "Nenhum documento corresponde à sua busca."
              : "Ainda não há documentos cadastrados."}
          </Typography>

          {!busca.trim() && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddCircleRounded />}
              onClick={abrirNovoDocumento}
              sx={botaoAdicionar}
            >
              Criar primeiro documento
            </Button>
          )}
        </Paper>
      ) : (
        <>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 1.5,
              border: "1px solid #d7e7f7",
              boxShadow: "none",
              overflow: "hidden",
            }}
          >
            <Table
              size="small"
              sx={{
                minWidth: 760,
                tableLayout: "fixed",
                "& .MuiTableCell-root": {
                  borderColor: "#dfe9f3",
                },
              }}
            >
              <TableHead
                sx={{
                  backgroundColor: "#edf6fe",
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      width: "30%",
                      px: 2,
                      py: 0.55,
                      borderRight: "1px solid #d9e7f3",
                    }}
                  >
                    {renderCabecalhoOrdenavel(
                      "Documento",
                      "nome",
                      DescriptionRounded,
                    )}
                  </TableCell>

                  <TableCell
                    sx={{
                      width: "54%",
                      px: 2,
                      py: 0.55,
                      borderRight: "1px solid #d9e7f3",
                    }}
                  >
                    {renderCabecalhoOrdenavel(
                      "Descrição",
                      "descricao",
                      SubjectRounded,
                    )}
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: "16%",
                      px: 1,
                      py: 0.55,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.7,
                        color: "#27456f",
                      }}
                    >
                      <SettingsRounded
                        sx={{
                          color: "#087cf0",
                          fontSize: 17,
                        }}
                      />

                      <Typography
                        variant="caption"
                        sx={{
                          color: "#27456f",
                          fontSize: ajustarTamanhoFonteSre("0.75rem"),
                          fontWeight: 700,
                        }}
                      >
                        Ações
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {documentosPaginados.map((doc) => (
                  <TableRow
                    key={doc.id}
                    hover
                    sx={{
                      height: 38,
                      "&:hover": {
                        backgroundColor: "#fbfdff",
                      },
                      "&:last-child td": {
                        borderBottom: 0,
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        px: 2,
                        py: 0.35,
                        borderRight: "1px solid #e5edf5",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.2,
                          minWidth: 0,
                        }}
                      >
                        <DescriptionRounded
                          sx={{
                            color: "#087cf0",
                            fontSize: 17,
                            flexShrink: 0,
                          }}
                        />

                        <Typography
                          title={doc.nome || "Documento sem nome"}
                          sx={{
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: "#0767d8",
                            fontSize: ajustarTamanhoFonteSre("0.78rem"),
                            fontWeight: 600,
                            textTransform: "uppercase",
                          }}
                        >
                          {doc.nome || "Documento sem nome"}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell
                      sx={{
                        px: 2,
                        py: 0.35,
                        borderRight: "1px solid #e5edf5",
                      }}
                    >
                      <Typography
                        title={doc.descricao || "Nenhuma descrição fornecida."}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: "#40597c",
                          fontSize: ajustarTamanhoFonteSre("0.77rem"),
                        }}
                      >
                        {doc.descricao || "Nenhuma descrição fornecida."}
                      </Typography>
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        px: 1,
                        py: 0.35,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 0.85,
                        }}
                      >
                        <Tooltip title="Editar" arrow>
                          <IconButton
                            size="small"
                            aria-label={`Editar ${doc.nome || "documento"}`}
                            onClick={() => handleEditarDocumento(doc.id)}
                            sx={ESTILO_ACAO_EDITAR}
                          >
                            <EditRounded sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Excluir" arrow>
                          <IconButton
                            size="small"
                            aria-label={`Excluir ${doc.nome || "documento"}`}
                            onClick={() => solicitarExclusao(doc)}
                            sx={ESTILO_ACAO_EXCLUIR}
                          >
                            <DeleteRounded sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Visualizar" arrow>
                          <IconButton
                            size="small"
                            aria-label={`Visualizar ${doc.nome || "documento"}`}
                            onClick={() => handleOpenDetails(doc)}
                            sx={ESTILO_ACAO_DETALHES}
                          >
                            <VisibilityRounded sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              alignItems: {
                xs: "flex-start",
                sm: "center",
              },
              justifyContent: "space-between",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              gap: 0.75,
              mt: 0.45,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#526b8d",
                fontSize: ajustarTamanhoFonteSre("0.72rem"),
              }}
            >
              {documentosFiltrados.length
                ? `Mostrando ${indiceInicial + 1} a ${indiceFinal} de ${
                    documentosFiltrados.length
                  } resultados`
                : "Mostrando 0 resultados"}
            </Typography>

            <Box
              sx={{
                ml: {
                  sm: "auto",
                },
              }}
            >
              <PaginacaoLista
                paginaAtual={paginaExibida}
                totalPaginas={totalPaginas}
                onChange={setPaginaAtual}
                ariaLabel="Paginação inferior da lista de documentos"
              />
            </Box>
          </Box>
        </>
      )}

      {/* =====================================================
           MODAL — NOVO / EDITAR DOCUMENTO
       ===================================================== */}

      <Dialog
        open={mostrarCadastro}
        onClose={handleFecharModalDocumento}
        fullWidth
        maxWidth="sm"
        TransitionProps={{
          onExited: limparFormularioDocumento,
        }}
        PaperProps={{
          sx: {
            width: {
              xs: "calc(100% - 24px)",
              sm: 640,
            },

            maxWidth: "640px !important",

            maxHeight: "92vh",

            display: "flex",
            flexDirection: "column",

            overflow: "hidden",

            borderRadius: 2.5,

            border: "1px solid #d7e7f7",
            borderTop: "5px solid #087cf0",

            boxShadow: "0 18px 48px rgba(20, 61, 106, 0.18)",
          },
        }}
      >
        <DialogTitle
          sx={{
            position: "relative",

            flexShrink: 0,

            px: {
              xs: 2.25,
              sm: 3,
            },

            pt: 2.4,
            pb: 1.7,
            pr: 7,

            borderBottom: "1px solid #edf2f7",

            backgroundColor: "#ffffff",
          }}
        >
          <Typography
            component="h2"
            sx={{
              color: "#0767d8",

              fontSize: {
                xs: ajustarTamanhoFonteSre("1.4rem"),
                sm: ajustarTamanhoFonteSre("1.6rem"),
              },

              fontWeight: 700,
              lineHeight: 1.15,
            }}
          >
            {documentoSelecionado !== null
              ? "Editar Documento"
              : "Novo Documento"}
          </Typography>

          <IconButton
            aria-label="Fechar"
            onClick={handleFecharModalDocumento}
            disabled={isSubmitting}
            sx={{
              position: "absolute",

              right: 16,
              top: 14,

              color: "#40597c",

              "&:hover": {
                color: "#0767d8",
                backgroundColor: "#eef6ff",
              },
            }}
          >
            <CloseRounded />
          </IconButton>
        </DialogTitle>

        <Box
          component="form"
          onSubmit={handleSubmitDocumento}
          sx={{
            minHeight: 0,

            display: "flex",
            flexDirection: "column",

            flex: 1,
          }}
        >
          <DialogContent
            sx={{
              flex: 1,

              overflowY: "auto",

              px: {
                xs: 2,
                sm: 2.5,
              },

              py: 2.25,

              backgroundColor: "#fbfdff",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",

                gap: 1.4,

                px: 1.6,
                py: 1.35,
                mb: 2.2,

                border: "1px solid #dceaf7",
                borderRadius: 1.8,

                backgroundColor: "#f3f8fd",
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,

                  flexShrink: 0,

                  display: "grid",
                  placeItems: "center",

                  borderRadius: 1.4,

                  color: "#087cf0",
                  backgroundColor: "#e3f2ff",
                }}
              >
                <DescriptionRounded
                  sx={{
                    fontSize: 24,
                  }}
                />
              </Box>

              <Box
                sx={{
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{
                    color: "#164f91",

                    fontSize: ajustarTamanhoFonteSre("0.9rem"),
                    fontWeight: 700,

                    lineHeight: 1.25,
                  }}
                >
                  Dados do documento
                </Typography>

                <Typography
                  sx={{
                    mt: 0.2,

                    color: "#71849d",

                    fontSize: ajustarTamanhoFonteSre("0.76rem"),
                    lineHeight: 1.4,
                  }}
                >
                  Preencha as informações principais do documento.
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                mb: 2,
              }}
            >
              <Typography
                component="label"
                htmlFor="documento-nome"
                sx={{
                  display: "block",

                  mb: 0.55,

                  color: "#314967",

                  fontSize: ajustarTamanhoFonteSre("0.78rem"),
                  fontWeight: 700,

                  lineHeight: 1.2,
                }}
              >
                Nome do documento
                <Box
                  component="span"
                  sx={{
                    ml: 0.35,
                    color: "#d32f2f",
                  }}
                >
                  *
                </Box>
              </Typography>

              <TextField
                id="documento-nome"
                fullWidth
                size="small"
                autoFocus
                placeholder="Digite o nome do documento..."
                value={nome}
                onChange={(event) => setNome(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TitleRounded
                        sx={{
                          fontSize: 19,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={ESTILO_CAMPO_DOCUMENTO}
              />
            </Box>

            <Box>
              <Typography
                component="label"
                htmlFor="documento-descricao"
                sx={{
                  display: "block",

                  mb: 0.55,

                  color: "#314967",

                  fontSize: ajustarTamanhoFonteSre("0.78rem"),
                  fontWeight: 700,

                  lineHeight: 1.2,
                }}
              >
                Descrição
              </Typography>

              <TextField
                id="documento-descricao"
                fullWidth
                multiline
                minRows={5}
                placeholder="Descreva o documento..."
                value={descricao}
                onChange={(event) => setDescricao(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      sx={{
                        alignSelf: "flex-start",
                        mt: 1.05,
                      }}
                    >
                      <SubjectRounded
                        sx={{
                          fontSize: 19,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  ...ESTILO_CAMPO_DOCUMENTO,

                  "& .MuiOutlinedInput-root": {
                    ...ESTILO_CAMPO_DOCUMENTO["& .MuiOutlinedInput-root"],
                    alignItems: "flex-start",
                  },
                }}
              />
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              flexShrink: 0,

              justifyContent: "space-between",

              gap: 1.25,

              px: {
                xs: 2,
                sm: 3,
              },

              py: 1.6,

              borderTop: "1px solid #e3edf6",

              backgroundColor: "#ffffff",

              boxShadow: "0 -4px 12px rgba(20, 61, 106, 0.035)",
            }}
          >
            <Button
              type="button"
              variant="outlined"
              startIcon={<CancelRounded />}
              onClick={handleVoltarParaDocumentos}
              disabled={isSubmitting}
              sx={botaoCancelar}
            >
              Cancelar
            </Button>

            <Button
              type="submit"
              variant={documentoSelecionado !== null ? "outlined" : "contained"}
              startIcon={
                isSubmitting ? null : (
                  <SaveRounded
                    sx={{
                      fontSize: "18px !important",
                    }}
                  />
                )
              }
              disabled={isSubmitting || !nome.trim()}
              sx={
                documentoSelecionado !== null
                  ? {
                      ...botaoSalvar,

                      minWidth: {
                        xs: 150,
                        sm: 175,
                      },
                    }
                  : {
                      ...botaoAdicionar,

                      minWidth: {
                        xs: 165,
                        sm: 195,
                      },
                    }
              }
            >
              {isSubmitting ? (
                <CircularProgress size={18} color="inherit" />
              ) : documentoSelecionado !== null ? (
                "Salvar alterações"
              ) : (
                "Cadastrar Documento"
              )}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* =====================================================
           MODAL — DETALHES DO DOCUMENTO
       ===================================================== */}

      <Dialog
        open={openDetails}
        onClose={handleCloseDetails}
        fullWidth
        maxWidth="sm"
        TransitionProps={{
          onExited: limparDetalhesDocumento,
        }}
        PaperProps={{
          sx: {
            width: {
              xs: "calc(100% - 24px)",
              sm: 600,
            },

            maxWidth: "600px !important",

            overflow: "hidden",

            borderRadius: 2.5,

            border: "1px solid #d7e7f7",
            borderTop: "5px solid #087cf0",

            boxShadow: "0 18px 48px rgba(20, 61, 106, 0.18)",
          },
        }}
      >
        <DialogTitle
          sx={{
            position: "relative",

            px: {
              xs: 2.25,
              sm: 3,
            },

            pt: 2.25,
            pb: 1.65,
            pr: 7,

            borderBottom: "1px solid #e6edf4",

            backgroundColor: "#ffffff",
          }}
        >
          <Typography
            component="h2"
            sx={{
              color: "#0767d8",

              fontSize: {
                xs: ajustarTamanhoFonteSre("1.35rem"),
                sm: ajustarTamanhoFonteSre("1.5rem"),
              },

              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            Detalhes do documento
          </Typography>

          <IconButton
            aria-label="Fechar"
            onClick={handleCloseDetails}
            sx={{
              position: "absolute",

              right: 14,
              top: 13,

              color: "#53657a",

              "&:hover": {
                color: "#0767d8",
                backgroundColor: "#eef6ff",
              },
            }}
          >
            <CloseRounded />
          </IconButton>
        </DialogTitle>

        <Tabs
          value={abaDetalhes}
          onChange={(_, novaAba) => setAbaDetalhes(novaAba)}
          variant="fullWidth"
          aria-label="Abas dos detalhes do documento"
          sx={{
            borderBottom: "1px solid #e6edf4",
            backgroundColor: "#ffffff",
            minHeight: 44,
            "& .MuiTab-root": {
              minHeight: 44,
              py: 1,
              px: 1,
              color: "#61758c",
              fontSize: ajustarTamanhoFonteSre("0.78rem"),
              fontWeight: 600,
              textTransform: "none",
            },
            "& .MuiTab-root.Mui-selected": {
              color: "#0767d8",
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#087cf0",
              height: 3,
            },
          }}
        >
          <Tab
            icon={<DescriptionRounded sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Dados do documento"
            id="aba-documento-dados"
            aria-controls="painel-documento-dados"
          />
          <Tab
            icon={<FolderCopy sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Processos vinculados"
            id="aba-documento-processos"
            aria-controls="painel-documento-processos"
          />
        </Tabs>

        <DialogContent
          sx={{
            px: {
              xs: 2,
              sm: 2.5,
            },

            py: 2.2,

            backgroundColor: "#fbfdff",
          }}
        >
          {abaDetalhes === 0 ? (
            <Box
              id="painel-documento-dados"
              role="tabpanel"
              aria-labelledby="aba-documento-dados"
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",

                  gap: 1.35,

                  px: 1.5,
                  py: 1.25,
                  mb: 2,

                  border: "1px solid #dceaf7",
                  borderRadius: 1.7,

                  backgroundColor: "#f3f8fd",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,

                    display: "grid",
                    placeItems: "center",

                    flexShrink: 0,

                    borderRadius: 1.3,

                    color: "#087cf0",
                    backgroundColor: "#e3f2ff",
                  }}
                >
                  <DescriptionRounded
                    sx={{
                      fontSize: 23,
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    minWidth: 0,
                  }}
                >
                  <Typography
                    sx={{
                      color: "#164f91",

                      fontSize: ajustarTamanhoFonteSre("0.88rem"),
                      fontWeight: 700,

                      lineHeight: 1.25,
                    }}
                  >
                    Dados do documento
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.15,

                      color: "#71849d",

                      fontSize: ajustarTamanhoFonteSre("0.74rem"),
                      lineHeight: 1.4,
                    }}
                  >
                    Informações cadastradas para este documento.
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  mb: 1.7,
                }}
              >
                <Typography
                  sx={{
                    mb: 0.55,

                    color: "#314967",

                    fontSize: ajustarTamanhoFonteSre("0.76rem"),
                    fontWeight: 700,

                    lineHeight: 1.3,
                  }}
                >
                  Nome do documento
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",

                    gap: 1,

                    minHeight: 44,

                    px: 1.35,
                    py: 1,

                    border: "1px solid #d7e2ed",
                    borderRadius: 1.3,

                    backgroundColor: "#ffffff",
                  }}
                >
                  <DescriptionRounded
                    sx={{
                      color: "#607995",
                      fontSize: 19,
                      flexShrink: 0,
                    }}
                  />

                  <Typography
                    sx={{
                      color: "#173f72",

                      fontSize: ajustarTamanhoFonteSre("0.8rem"),
                      fontWeight: 700,

                      lineHeight: 1.45,

                      textTransform: "uppercase",

                      wordBreak: "break-word",
                    }}
                  >
                    {detalhesDocumento?.nome || "Documento sem nome"}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography
                  sx={{
                    mb: 0.55,

                    color: "#314967",

                    fontSize: ajustarTamanhoFonteSre("0.76rem"),
                    fontWeight: 700,

                    lineHeight: 1.3,
                  }}
                >
                  Descrição
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",

                    gap: 1,

                    minHeight: 92,

                    px: 1.35,
                    py: 1.15,

                    border: "1px solid #d7e2ed",
                    borderRadius: 1.3,

                    backgroundColor: "#ffffff",
                  }}
                >
                  <SubjectRounded
                    sx={{
                      mt: 0.1,

                      color: "#607995",

                      fontSize: 19,

                      flexShrink: 0,
                    }}
                  />

                  <Typography
                    sx={{
                      color: "#40546d",

                      fontSize: ajustarTamanhoFonteSre("0.79rem"),
                      lineHeight: 1.55,

                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {detalhesDocumento?.descricao ||
                      "Nenhuma descrição fornecida."}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              id="painel-documento-processos"
              role="tabpanel"
              aria-labelledby="aba-documento-processos"
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.35,
                  px: 1.5,
                  py: 1.25,
                  mb: 2,
                  border: "1px solid #dceaf7",
                  borderRadius: 1.7,
                  backgroundColor: "#f3f8fd",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    borderRadius: 1.3,
                    color: "#087cf0",
                    backgroundColor: "#e3f2ff",
                  }}
                >
                  <FolderCopy sx={{ fontSize: 23 }} />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: "#164f91",
                      fontSize: ajustarTamanhoFonteSre("0.88rem"),
                      fontWeight: 700,
                      lineHeight: 1.25,
                    }}
                  >
                    Processos vinculados
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.15,
                      color: "#71849d",
                      fontSize: ajustarTamanhoFonteSre("0.74rem"),
                      lineHeight: 1.4,
                    }}
                  >
                    Tipos de processo que exigem este documento.
                  </Typography>
                </Box>
              </Box>

              {carregandoProcessosDetalhes ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.25,
                    minHeight: 110,
                  }}
                >
                  <CircularProgress size={21} />

                  <Typography
                    sx={{
                      color: "#60748d",
                      fontSize: ajustarTamanhoFonteSre("0.78rem"),
                    }}
                  >
                    Consultando processos vinculados...
                  </Typography>
                </Box>
              ) : erroProcessosDetalhes ? (
                <Alert severity="error" sx={{ borderRadius: 1.5 }}>
                  Não foi possível consultar os processos vinculados a este
                  documento. Tente novamente ao reabrir a visualização.
                </Alert>
              ) : processosDetalhes.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.2,
                    px: 1.5,
                    py: 1.4,
                    border: "1px solid #d8e3ed",
                    borderRadius: 1.5,
                    backgroundColor: "#f7f9fb",
                  }}
                >
                  <InfoRounded
                    sx={{
                      color: "#607995",
                      fontSize: 22,
                      flexShrink: 0,
                    }}
                  />

                  <Box>
                    <Typography
                      sx={{
                        color: "#314967",
                        fontSize: ajustarTamanhoFonteSre("0.8rem"),
                        fontWeight: 700,
                      }}
                    >
                      Nenhum processo vinculado encontrado.
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.4,
                        color: "#60748d",
                        fontSize: ajustarTamanhoFonteSre("0.76rem"),
                        lineHeight: 1.5,
                      }}
                    >
                      Este documento não está vinculado a nenhum tipo de
                      processo cadastrado.
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.4,
                      py: 1,
                      mb: 1.1,
                      border: "1px solid #d8e3ed",
                      borderRadius: 1.5,
                      backgroundColor: "#f7f9fb",
                    }}
                  >
                    <InfoRounded
                      sx={{
                        color: "#527295",
                        fontSize: 20,
                      }}
                    />

                    <Typography
                      sx={{
                        color: "#52667e",
                        fontSize: ajustarTamanhoFonteSre("0.77rem"),
                      }}
                    >
                      Este documento está vinculado a{" "}
                      <strong>
                        {processosDetalhes.length}{" "}
                        {processosDetalhes.length === 1
                          ? "tipo de processo"
                          : "tipos de processo"}
                      </strong>
                      .
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.85,
                      maxHeight: 260,
                      overflowY: "auto",
                      pr: 0.25,
                    }}
                  >
                    {processosDetalhes.map((processo) => {
                      const destacado =
                        processoDestacadoId !== null &&
                        Number(processo.processo_id) ===
                          Number(processoDestacadoId);

                      return (
                        <Box
                          key={processo.processo_id}
                          ref={destacado ? processoDestacadoRef : null}
                          component={RouterLink}
                          to="/admin/processo"
                          state={{
                            processoIdParaVisualizar: processo.processo_id,
                            documentoIdParaDestacar: detalhesDocumento?.id,
                          }}
                          aria-label={`Visualizar processo ${processo.processo_nome || processo.processo_id}`}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.1,
                            px: 1.35,
                            py: 1.15,
                            border: destacado
                              ? "2px solid #087cf0"
                              : "1px solid #d7e8f8",
                            borderLeft: "4px solid #087cf0",
                            borderRadius: 1.4,
                            backgroundColor: destacado ? "#eaf4ff" : "#f2f8fe",
                            textDecoration: "none",
                            cursor: "pointer",
                            "&:hover": {
                              filter: "brightness(0.97)",
                            },
                            "&:focus-visible": {
                              outline: "2px solid #087cf0",
                              outlineOffset: 2,
                            },
                          }}
                        >
                          <FolderCopy
                            sx={{
                              color: "#087cf0",
                              fontSize: 21,
                              flexShrink: 0,
                              mt: 0.1,
                            }}
                          />

                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                color: "#173a67",
                                fontSize: ajustarTamanhoFonteSre("0.78rem"),
                                fontWeight: 700,
                                lineHeight: 1.45,
                                wordBreak: "break-word",
                              }}
                            >
                              Processo {processo.processo_id} —{" "}
                              {processo.processo_nome || "Processo sem nome"}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* =====================================================
           MODAL — IMPACTO DA EXCLUSÃO
       ===================================================== */}

      <Dialog
        open={confirmacaoExclusaoAberta}
        onClose={fecharConfirmacaoExclusao}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            width: {
              xs: "calc(100% - 24px)",
              sm: 620,
            },

            maxWidth: "620px !important",

            maxHeight: "92vh",

            display: "flex",
            flexDirection: "column",

            overflow: "hidden",

            borderRadius: 2.5,

            border: "1px solid #dbe3ec",
            borderTop: "5px solid #e32636",

            boxShadow: "0 18px 48px rgba(28, 48, 72, 0.18)",
          },
        }}
      >
        <DialogTitle
          sx={{
            position: "relative",

            display: "flex",
            alignItems: "center",

            gap: 1.35,

            px: 2.5,
            pt: 2,
            pb: 1.55,
            pr: 7,

            borderBottom: "1px solid #e5ebf1",

            backgroundColor: "#ffffff",
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,

              display: "grid",
              placeItems: "center",

              flexShrink: 0,

              borderRadius: 1.3,

              color: "#d52231",
              backgroundColor: "#fdecee",
            }}
          >
            <WarningAmberRounded
              sx={{
                fontSize: 24,
              }}
            />
          </Box>

          <Typography
            component="h2"
            sx={{
              color: "#1f2f46",

              fontSize: ajustarTamanhoFonteSre("1.28rem"),
              fontWeight: 700,

              lineHeight: 1.2,
            }}
          >
            Excluir documento
          </Typography>

          <IconButton
            aria-label="Fechar"
            onClick={fecharConfirmacaoExclusao}
            disabled={isDeleting}
            sx={{
              position: "absolute",

              right: 14,
              top: 13,

              color: "#53657a",

              "&:hover": {
                color: "#1f2f46",
                backgroundColor: "#f3f6f9",
              },
            }}
          >
            <CloseRounded />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            flex: 1,

            overflowY: "auto",

            px: 2.5,
            py: 2,

            backgroundColor: "#ffffff",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",

              gap: 1.2,

              px: 1.55,
              py: 1.35,
              mb: 1.65,

              border: "1px solid #f2b9bf",
              borderRadius: 1.5,

              backgroundColor: "#fff4f5",
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,

                display: "grid",
                placeItems: "center",

                flexShrink: 0,

                borderRadius: "50%",

                color: "#ffffff",

                backgroundColor: "#e32636",
              }}
            >
              <WarningAmberRounded
                sx={{
                  fontSize: 19,
                }}
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  mb: 0.25,

                  color: "#c71f2e",

                  fontSize: ajustarTamanhoFonteSre("0.82rem"),
                  fontWeight: 700,

                  lineHeight: 1.35,
                }}
              >
                Atenção: esta é uma ação permanente.
              </Typography>

              <Typography
                component="div"
                sx={{
                  color: "#50627a",

                  fontSize: ajustarTamanhoFonteSre("0.77rem"),
                  lineHeight: 1.5,
                }}
              >
                Ao continuar, o cadastro deste documento será{" "}
                <strong>excluído permanentemente</strong> do sistema e a ação{" "}
                <strong>não poderá ser desfeita</strong>.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",

              gap: 1.15,

              px: 1.45,
              py: 1.2,
              mb: 1.55,

              border: "1px solid #cfe1f3",
              borderRadius: 1.5,

              backgroundColor: "#f2f8fe",
            }}
          >
            <Box
              sx={{
                width: 38,
                height: 38,

                display: "grid",
                placeItems: "center",

                flexShrink: 0,

                borderRadius: 1.2,

                color: "#31577f",
                backgroundColor: "#e2eef9",
              }}
            >
              <DescriptionRounded
                sx={{
                  fontSize: 21,
                }}
              />
            </Box>

            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  color: "#718196",

                  fontSize: ajustarTamanhoFonteSre("0.69rem"),
                  fontWeight: 500,

                  lineHeight: 1.25,
                }}
              >
                Documento a ser excluído
              </Typography>

              <Typography
                sx={{
                  mt: 0.15,

                  color: "#153f73",

                  fontSize: ajustarTamanhoFonteSre("0.78rem"),
                  fontWeight: 700,

                  lineHeight: 1.35,

                  textTransform: "uppercase",

                  wordBreak: "break-word",
                }}
              >
                {documentoParaExcluir?.nome || "Documento"}
              </Typography>
            </Box>
          </Box>

          {/* PROCESSOS VINCULADOS AO DOCUMENTO */}
          <Box sx={{ pt: 0.15, borderTop: "1px solid #e5ebf1" }}>
            <Typography
              sx={{
                mt: 0.9,
                mb: 0.8,
                color: "#1f2f46",
                fontSize: ajustarTamanhoFonteSre("0.84rem"),
                fontWeight: 700,
              }}
            >
              Processos vinculados
            </Typography>

            {isLoadingProcessosVinculados ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  minHeight: 82,
                  border: "1px solid #dce7f1",
                  borderRadius: 1.5,
                  backgroundColor: "#f8fafc",
                }}
              >
                <CircularProgress size={20} />
                <Typography sx={{ color: "#60748d", fontSize: ajustarTamanhoFonteSre("0.77rem") }}>
                  Verificando processos vinculados...
                </Typography>
              </Box>
            ) : erroConsultaProcessosVinculados ? (
              <Alert severity="error" sx={{ borderRadius: 1.5 }}>
                Não foi possível consultar os vínculos deste documento. Por
                segurança, a exclusão foi bloqueada.
              </Alert>
            ) : tiposProcessosVinculados.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.4,
                  py: 1.1,
                  border: "1px solid #d8e3ed",
                  borderRadius: 1.5,
                  backgroundColor: "#f7f9fb",
                }}
              >
                <InfoRounded sx={{ color: "#607995", fontSize: 20 }} />
                <Typography sx={{ color: "#52667e", fontSize: ajustarTamanhoFonteSre("0.77rem") }}>
                  Nenhum tipo de processo vinculado a este documento.
                </Typography>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.4,
                    py: 1,
                    mb: 1.1,
                    border: "1px solid #d8e3ed",
                    borderRadius: 1.5,
                    backgroundColor: "#f7f9fb",
                  }}
                >
                  <InfoRounded sx={{ color: "#527295", fontSize: 20 }} />
                  <Typography sx={{ color: "#52667e", fontSize: ajustarTamanhoFonteSre("0.77rem") }}>
                    Este documento está vinculado a{" "}
                    <strong>
                      {tiposProcessosVinculados.length}{" "}
                      {tiposProcessosVinculados.length === 1
                        ? "tipo de processo"
                        : "tipos de processo"}
                    </strong>
                    .
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.85,
                    maxHeight: 260,
                    overflowY: "auto",
                    pr: 0.25,
                  }}
                >
                  {tiposProcessosVinculados.map((processo) => (
                    <Box
                      key={processo.processo_id}
                      component={RouterLink}
                      to="/admin/processo"
                      state={{
                        processoIdParaVisualizar: processo.processo_id,
                        documentoIdParaDestacar: documentoParaExcluir?.id,
                      }}
                      aria-label={`Visualizar processo ${processo.processo_nome || processo.processo_id}`}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.1,
                        px: 1.35,
                        py: 1.15,
                        border: "1px solid #d7e8f8",
                        borderLeft: "4px solid #087cf0",
                        borderRadius: 1.4,
                        backgroundColor: "#f2f8fe",
                        textDecoration: "none",
                        cursor: "pointer",
                        "&:hover": {
                          filter: "brightness(0.97)",
                        },
                        "&:focus-visible": {
                          outline: "2px solid #087cf0",
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <FolderCopy
                        sx={{
                          color: "#087cf0",
                          fontSize: 21,
                          flexShrink: 0,
                          mt: 0.1,
                        }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: "#173a67",
                            fontSize: ajustarTamanhoFonteSre("0.78rem"),
                            fontWeight: 700,
                            lineHeight: 1.45,
                            wordBreak: "break-word",
                          }}
                        >
                          Processo {processo.processo_id} —{" "}
                          {processo.processo_nome || "Processo sem nome"}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Box>

          {!isLoadingProcessosVinculados &&
            !erroConsultaProcessosVinculados && (
              <Typography
                component="div"
                sx={{
                  mt: 1.35,

                  color: "#52667e",

                  fontSize: ajustarTamanhoFonteSre("0.75rem"),
                  lineHeight: 1.5,
                }}
              >
                Antes de confirmar, verifique se a exclusão é{" "}
                <strong>realmente necessária</strong>.
              </Typography>
            )}
        </DialogContent>

        <DialogActions
          sx={{
            flexShrink: 0,

            justifyContent: "space-between",

            gap: 1.25,

            px: 2.5,
            py: 1.45,

            borderTop: "1px solid #e5ebf1",

            backgroundColor: "#ffffff",
          }}
        >
          <Button
            type="button"
            variant="outlined"
            startIcon={<ArrowBackRounded />}
            onClick={fecharConfirmacaoExclusao}
            disabled={isDeleting}
            sx={botaoVoltar}
          >
            Voltar
          </Button>

          <Button
            type="button"
            variant="contained"
            startIcon={
              <DeleteRounded
                sx={{
                  fontSize: "18px !important",
                }}
              />
            }
            onClick={abrirConfirmacaoFinalExclusao}
            disabled={
              isDeleting ||
              isLoadingProcessosVinculados ||
              erroConsultaProcessosVinculados
            }
            sx={{
              ...botaoExcluir,
              minWidth: 160,
            }}
          >
            Excluir documento
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
           MODAL — CONFIRMAÇÃO FINAL DA EXCLUSÃO
       ===================================================== */}

      <Dialog
        open={confirmacaoFinalAberta}
        onClose={fecharConfirmacaoFinalExclusao}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            width: {
              xs: "calc(100% - 24px)",
              sm: 500,
            },

            maxWidth: "500px !important",

            overflow: "hidden",

            borderRadius: 2.5,

            border: "1px solid #dbe3ec",
            borderTop: "5px solid #e32636",

            boxShadow: "0 18px 48px rgba(28, 48, 72, 0.2)",
          },
        }}
      >
        <DialogTitle
          sx={{
            position: "relative",

            display: "flex",
            alignItems: "center",

            gap: 1.25,

            px: 2.5,
            pt: 2,
            pb: 1.55,
            pr: 7,

            borderBottom: "1px solid #e5ebf1",

            backgroundColor: "#ffffff",
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,

              display: "grid",
              placeItems: "center",

              flexShrink: 0,

              borderRadius: 1.3,

              color: "#d52231",
              backgroundColor: "#fdecee",
            }}
          >
            <WarningAmberRounded
              sx={{
                fontSize: 24,
              }}
            />
          </Box>

          <Typography
            component="h2"
            sx={{
              color: "#1f2f46",

              fontSize: ajustarTamanhoFonteSre("1.2rem"),
              fontWeight: 700,

              lineHeight: 1.2,
            }}
          >
            Confirmação final
          </Typography>

          <IconButton
            aria-label="Fechar"
            onClick={fecharConfirmacaoFinalExclusao}
            disabled={isDeleting}
            sx={{
              position: "absolute",

              right: 14,
              top: 13,

              color: "#53657a",

              "&:hover": {
                color: "#1f2f46",
                backgroundColor: "#f3f6f9",
              },
            }}
          >
            <CloseRounded />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            px: 2.5,
            py: 2,

            backgroundColor: "#ffffff",
          }}
        >
          <Typography
            component="div"
            sx={{
              color: "#40546d",

              fontSize: ajustarTamanhoFonteSre("0.82rem"),
              lineHeight: 1.55,
            }}
          >
            Você está prestes a excluir permanentemente o documento:
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",

              gap: 1.1,

              px: 1.4,
              py: 1.2,

              my: 1.4,

              border: "1px solid #cfe1f3",
              borderRadius: 1.5,

              backgroundColor: "#f2f8fe",
            }}
          >
            <Box
              sx={{
                width: 38,
                height: 38,

                display: "grid",
                placeItems: "center",

                flexShrink: 0,

                borderRadius: 1.2,

                color: "#31577f",
                backgroundColor: "#e2eef9",
              }}
            >
              <DescriptionRounded
                sx={{
                  fontSize: 21,
                }}
              />
            </Box>

            <Typography
              sx={{
                color: "#153f73",

                fontSize: ajustarTamanhoFonteSre("0.78rem"),
                fontWeight: 700,

                lineHeight: 1.4,

                textTransform: "uppercase",

                wordBreak: "break-word",
              }}
            >
              {documentoParaExcluir?.nome || "Documento"}
            </Typography>
          </Box>

          <Box
            sx={{
              px: 1.5,
              py: 1.2,
              mb: 1.5,

              border: "1px solid #f2c4c8",
              borderRadius: 1.5,

              backgroundColor: "#fff7f7",
            }}
          >
            <Typography
              component="div"
              sx={{
                color: "#53657a",

                fontSize: ajustarTamanhoFonteSre("0.78rem"),
                lineHeight: 1.55,
              }}
            >
              Esta ação <strong>não poderá ser desfeita</strong>. Confirme
              abaixo somente se realmente deseja prosseguir com a exclusão.
            </Typography>
          </Box>

          <Box
            sx={{
              px: 1.4,
              py: 1.1,

              border: "1px solid #dce4ec",
              borderRadius: 1.5,

              backgroundColor: "#f8fafc",
            }}
          >
            <FormControlLabel
              sx={{
                m: 0,

                alignItems: "flex-start",

                "& .MuiFormControlLabel-label": {
                  pt: 0.45,
                },
              }}
              control={
                <Checkbox
                  checked={confirmacaoFinalCiente}
                  onChange={(event) =>
                    setConfirmacaoFinalCiente(event.target.checked)
                  }
                  disabled={isDeleting}
                  sx={{
                    pt: 0.2,

                    color: "#708399",

                    "&.Mui-checked": {
                      color: "#d32f2f",
                    },
                  }}
                />
              }
              label={
                <Typography
                  component="div"
                  sx={{
                    color: "#40546d",

                    fontSize: ajustarTamanhoFonteSre("0.78rem"),
                    lineHeight: 1.5,
                  }}
                >
                  Estou ciente de que desejo{" "}
                  <strong>excluir definitivamente este documento</strong>.
                </Typography>
              }
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "space-between",

            gap: 1.25,

            px: 2.5,
            py: 1.45,

            borderTop: "1px solid #e5ebf1",

            backgroundColor: "#ffffff",
          }}
        >
          <Button
            type="button"
            variant="outlined"
            startIcon={<ArrowBackRounded />}
            onClick={voltarParaImpactoExclusao}
            disabled={isDeleting}
            sx={botaoVoltar}
          >
            Voltar
          </Button>

          <Button
            type="button"
            variant="contained"
            startIcon={
              isDeleting ? null : (
                <DeleteRounded
                  sx={{
                    fontSize: "18px !important",
                  }}
                />
              )
            }
            onClick={handleExcluirDocumento}
            disabled={!confirmacaoFinalCiente || isDeleting}
            sx={{
              ...botaoExcluir,
              minWidth: 165,
            }}
          >
            {isDeleting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "Confirmar exclusão"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
           FEEDBACK
       ===================================================== */}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          elevation={6}
          sx={{
            width: "100%",
            alignItems: "center",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CadastroDocumentosPage;
